import React, { useEffect, useState, useContext, useRef, useLayoutEffect, createContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ChevronRight, ChevronDown, Link2, ImagePlus, Copy, Check, CheckCircle2, Plus, Lock, MoreHorizontal } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { SUCCESS_FOUNDATIONS, PAGES } from "./programmeContent";
import { RichTextField } from "../../components/RichTextField";
import { ProgIcon } from "../../components/ProgIcon";
import { StickyBack } from "../../components/StickyBack";

import { toHtml, htmlToText } from "../../lib/richtext";
import { ReadMore } from "../../components/ReadMore";

const hasAnn = (a) => typeof a === "string" && (htmlToText(a).trim() !== "" || a.includes("data-cid"));
const DARK = { backgroundColor: "#2D181E", backgroundImage: "radial-gradient(circle at 100% 0%, rgba(123,17,58,0.55), transparent 60%)" };

// ---------------------------------------------------------------------------
// Submissions store — persists client answers to MongoDB (not localStorage).
// Shared by the client programme page (read/write) and the admin dossier
// viewer (read-only), so Fanny can see exactly what each client filled in.
// ---------------------------------------------------------------------------
const SubmissionsContext = createContext(null);

function useSub() {
  return useContext(SubmissionsContext) || { get: () => undefined, save: () => {}, uploadFile: async () => "", readOnly: false, loaded: true, annotate: false, getAnn: () => undefined, saveAnn: () => {}, annLoaded: true };
}

export function SubmissionsProvider({ page, readOnly = false, clientId = null, annotate = false, children }) {
  const [map, setMap] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [annMap, setAnnMap] = useState({});
  const [annLoaded, setAnnLoaded] = useState(false);
  const [comments, setComments] = useState([]);
  const [admin, setAdmin] = useState({ name: "Growithfanny", photo: "/fanny.png" });
  const timers = useRef({});
  const annTimers = useRef({});
  const cmtTimers = useRef({});
  useEffect(() => {
    let active = true;
    setLoaded(false);
    const url = clientId
      ? `/admin/clients/${clientId}/submissions/${page}`
      : `/client/submissions/${page}`;
    api.get(url)
      .then(({ data }) => { if (active) { setMap(data || {}); setLoaded(true); } })
      .catch(() => { if (active) setLoaded(true); });
    return () => { active = false; };
  }, [page, clientId]);
  useEffect(() => {
    if (!annotate || !clientId) { setAnnLoaded(true); return; }
    let active = true;
    setAnnLoaded(false);
    const stripOrphans = (html, valid) =>
      typeof html === "string"
        ? html.replace(/<span([^>]*)data-cid="([^"]*)"([^>]*)>([\s\S]*?)<\/span>/g, (m, a, id, b, inner) => (valid.has(id) ? m : inner))
        : html;
    Promise.all([
      api.get(`/admin/clients/${clientId}/annotations/${page}`).then((r) => r.data).catch(() => ({})),
      api.get(`/admin/clients/${clientId}/comments/${page}`).then((r) => r.data).catch(() => []),
    ]).then(([ann, cmts]) => {
      if (!active) return;
      const list = cmts || [];
      const valid = new Set(list.map((c) => c.id));
      const annData = ann || {};
      const cleaned = {};
      Object.entries(annData).forEach(([k, v]) => {
        const nv = stripOrphans(v, valid);
        cleaned[k] = nv;
        if (nv !== v) api.put(`/admin/clients/${clientId}/annotations/${page}`, { key: k, value: nv }).catch(() => {});
      });
      setAnnMap(cleaned);
      setComments(list);
      setAnnLoaded(true);
    });
    api.get(`/admin/profile`)
      .then(({ data }) => { if (active && data) setAdmin({ name: "Growithfanny", photo: data.photo || "/fanny.png" }); })
      .catch(() => {});
    return () => { active = false; };
  }, [annotate, clientId, page]);
  useEffect(() => {
    if (annotate && clientId) return;
    if (clientId || readOnly) { setComments([]); return; }
    api.get(`/client/comments/${page}`).then(({ data }) => setComments(data || [])).catch(() => {});
  }, [annotate, clientId, readOnly, page]);
  const cBase = clientId ? `/admin/clients/${clientId}/comments/${page}` : `/client/comments/${page}`;
  const viewer = clientId ? "admin" : "cliente";
  const get = (k) => map[k];
  const save = (k, v) => {
    if (readOnly) return;
    setMap((m) => ({ ...m, [k]: v }));
    clearTimeout(timers.current[k]);
    timers.current[k] = setTimeout(() => {
      api.put(`/client/submissions/${page}`, { key: k, value: v }).catch(() => {});
    }, 600);
  };
  const getAnn = (k) => annMap[k];
  const saveAnn = (k, html) => {
    setAnnMap((m) => ({ ...m, [k]: html }));
    clearTimeout(annTimers.current[k]);
    annTimers.current[k] = setTimeout(() => {
      api.put(`/admin/clients/${clientId}/annotations/${page}`, { key: k, value: html }).catch(() => {});
    }, 700);
  };
  const addComment = ({ id, key, quote, text = "" }) => {
    setComments((c) => [...c, { id, key, quote, text, validated: !!text, author: viewer, author_name: viewer === "admin" ? "Fanny" : "Moi", created_at: new Date().toISOString() }]);
    api.post(cBase, { id, key, quote, text }).catch(() => {});
  };
  const updateComment = (id, text) => {
    setComments((c) => c.map((x) => (x.id === id ? { ...x, text } : x)));
    clearTimeout(cmtTimers.current[id]);
    cmtTimers.current[id] = setTimeout(() => {
      api.put(`${cBase}/${id}`, { text }).catch(() => {});
    }, 600);
  };
  const validateComment = (id, text, validated) => {
    setComments((c) => c.map((x) => (x.id === id ? { ...x, text, validated } : x)));
    clearTimeout(cmtTimers.current[id]);
    api.put(`${cBase}/${id}`, { text, validated }).catch(() => {});
  };
  const removeComment = (id) => {
    const cmt = comments.find((x) => x.id === id);
    setComments((c) => c.filter((x) => x.id !== id));
    api.delete(`${cBase}/${id}`).catch(() => {});
    const span = document.querySelector(`[data-cid="${id}"]`);
    if (span) {
      const field = span.closest("[contenteditable]");
      const p = span.parentNode;
      while (span.firstChild) p.insertBefore(span.firstChild, span);
      p.removeChild(span);
      if (cmt && cmt.key && field) (annotate ? saveAnn : save)(cmt.key, field.innerHTML);
    }
  };
  const uploadFile = async (raw) => {
    const file = await compressImage(raw);
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post(`/client/submissions-upload/${page}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data.url;
  };
  return (
    <SubmissionsContext.Provider value={{ get, save, uploadFile, readOnly, loaded, annotate, getAnn, saveAnn, annLoaded, comments, addComment, updateComment, validateComment, removeComment, admin, viewer }}>
      {children}
    </SubmissionsContext.Provider>
  );
}

function ytEmbed(url) {
  const m = url.match(/(?:youtu\.be\/|v=|embed\/)([A-Za-z0-9_-]+)/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : url;
}

function renderRich(str) {
  if (typeof str !== "string") return str;
  return str.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith("**") && part.endsWith("**")
      ? <strong key={i} className="font-medium text-ink">{part.slice(2, -2)}</strong>
      : part
  );
}

function Answer({ storageKey, boxClass, tall = false, placeholder = "Ta réponse…" }) {
  const { get, save, readOnly, loaded, annotate, getAnn, saveAnn, annLoaded, addComment } = useSub();
  const [val, setVal] = useState("");
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (!loaded) return;
    setReady(true);
    const dbVal = get(storageKey);
    if (dbVal !== undefined && dbVal !== "") { setVal(dbVal); return; }
    if (!readOnly) {
      const ls = localStorage.getItem(storageKey);
      if (ls != null && ls !== "") { setVal(ls); save(storageKey, ls); return; }
    }
    setVal(dbVal ?? "");
  }, [loaded, storageKey]);
  if (annotate) {
    if (!loaded || !annLoaded) return <div className="mt-2 h-11 rounded-xl border border-line bg-white" />;
    const a = getAnn(storageKey);
    const initial = hasAnn(a) ? a : toHtml(get(storageKey) ?? "");
    return <div className="mt-2"><RichTextField testid={`annotate-${storageKey}`} fieldKey={storageKey} onComment={addComment} initialHtml={initial} onChange={(html) => saveAnn(storageKey, html)} /></div>;
  }
  if (readOnly) {
    return (
      <div data-testid={`answer-ro-${storageKey}`} className={`mt-2 w-full whitespace-pre-wrap rounded-xl border p-3 text-sm ${boxClass || "border-line bg-cream/40"}`}>
        {val ? <span dangerouslySetInnerHTML={{ __html: toHtml(val) }} /> : <span className="italic text-ink-muted">— Pas encore de réponse —</span>}
      </div>
    );
  }
  if (!ready) return <div className="mt-2 h-[86px] rounded-xl border border-line bg-cream/40" />;
  return (
    <div className="mt-2">
      <RichTextField
        testid={`answer-${storageKey}`}
        fieldKey={storageKey}
        initialHtml={val}
        onChange={(html) => { setVal(html); save(storageKey, html); }}
        onComment={addComment}
        placeholder={placeholder}
        className={`${tall ? "!min-h-[440px] !p-5 leading-relaxed" : "min-h-[86px]"} rounded-xl border p-3 text-sm text-ink ${boxClass || (tall ? "border-line bg-white focus:border-bordeaux" : "border-line bg-cream/40 focus:border-bordeaux focus:bg-white")}`}
      />
    </div>
  );
}

function EditableLine({ storageKey, defaultValue, rows = 2 }) {
  const { get, save, readOnly, loaded, annotate, getAnn, saveAnn, annLoaded, addComment } = useSub();
  const [val, setVal] = useState(defaultValue);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (!loaded) return;
    setReady(true);
    const s = get(storageKey);
    if (s !== undefined) { setVal(s); return; }
    if (!readOnly) {
      const ls = localStorage.getItem(storageKey);
      if (ls != null) { setVal(ls); save(storageKey, ls); return; }
    }
    setVal(defaultValue);
  }, [loaded, storageKey]);
  if (annotate) {
    if (!loaded || !annLoaded) return <div className="h-11 rounded-xl border border-line bg-white" />;
    const a = getAnn(storageKey);
    const initial = hasAnn(a) ? a : toHtml(get(storageKey) ?? defaultValue ?? "");
    return <RichTextField testid={`annotate-${storageKey}`} fieldKey={storageKey} onComment={addComment} initialHtml={initial} onChange={(html) => saveAnn(storageKey, html)} />;
  }
  if (readOnly) {
    return (
      <div data-testid={`editable-ro-${storageKey}`} className="w-full whitespace-pre-wrap rounded-xl border border-line bg-white p-3 text-sm font-light leading-relaxed text-ink-soft" dangerouslySetInnerHTML={{ __html: toHtml(val) }} />
    );
  }
  if (!ready) return <div className="h-[46px] rounded-xl border border-line bg-white" />;
  return (
    <RichTextField
      testid={`editable-${storageKey}`}
      fieldKey={storageKey}
      initialHtml={val}
      onChange={(html) => { setVal(html); save(storageKey, html); }}
      onComment={addComment}
      className="rounded-xl border border-line bg-white p-3 text-sm font-light leading-relaxed text-ink-soft focus:border-bordeaux"
    />
  );
}

function EditFields({ title, lines, fields, slug, index }) {
  return (
    <div className="space-y-4 rounded-2xl border border-bordeaux-border bg-bordeaux-tint/30 p-5">
      {title && <p className="font-medium text-bordeaux">{title}</p>}
      {fields && fields.map((f, i) => (
        <div key={i}>
          <p className="mb-1.5 text-sm font-semibold text-bordeaux">{f.label}</p>
          <EditableLine storageKey={`pp-${slug}-${index}-${i}`} defaultValue={f.value} rows={f.rows || 3} />
        </div>
      ))}
      {lines && lines.map((ln, i) => (
        <EditableLine key={i} storageKey={`pp-${slug}-${index}-${i}`} defaultValue={ln} />
      ))}
    </div>
  );
}

function TableCell({ storageKey, defaultValue }) {
  const { get, save, readOnly, loaded, annotate, getAnn, saveAnn, annLoaded, addComment } = useSub();
  const [val, setVal] = useState(defaultValue);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (!loaded) return;
    setReady(true);
    const s = get(storageKey);
    if (s !== undefined) { setVal(s); return; }
    if (!readOnly) {
      const ls = localStorage.getItem(storageKey);
      if (ls != null) { setVal(ls); save(storageKey, ls); return; }
    }
    setVal(defaultValue);
  }, [loaded, storageKey]);
  if (annotate) {
    if (!loaded || !annLoaded) return <div className="block w-full p-3 text-sm text-ink-muted">…</div>;
    const a = getAnn(storageKey);
    const initial = hasAnn(a) ? a : toHtml(get(storageKey) ?? defaultValue ?? "");
    return <div className="p-1"><RichTextField testid={`annotate-${storageKey}`} fieldKey={storageKey} onComment={addComment} initialHtml={initial} onChange={(html) => saveAnn(storageKey, html)} /></div>;
  }
  if (readOnly) {
    return (
      <div className="block w-full whitespace-pre-wrap p-3 text-sm font-light leading-relaxed text-ink-soft">
        {val ? <span dangerouslySetInnerHTML={{ __html: toHtml(val) }} /> : <span className="italic text-ink-muted">—</span>}
      </div>
    );
  }
  if (!ready) return <div className="block min-h-[46px] w-full bg-white" />;
  return (
    <RichTextField
      testid={`cell-${storageKey}`}
      fieldKey={storageKey}
      initialHtml={val}
      onChange={(html) => { setVal(html); save(storageKey, html); }}
      onComment={addComment}
      className="block bg-white p-3 text-sm font-light leading-relaxed text-ink-soft focus:bg-bordeaux-tint/10"
    />
  );
}

function EditableTable({ columns, rows, slug, index }) {
  const { get, save, readOnly, loaded, annotate, getAnn, saveAnn, annLoaded, addComment } = useSub();
  const countKey = `pp-${slug}-${index}-rows`;
  const cellKey = (ri, ci) => `pp-${slug}-${index}-r${ri}-c${ci}`;
  const [rowCount, setRowCount] = useState(rows.length);
  const [version, setVersion] = useState(0);
  useEffect(() => {
    if (!loaded) return;
    let s = get(countKey);
    if (s === undefined && !readOnly) {
      const ls = localStorage.getItem(countKey);
      if (ls != null) { s = ls; save(countKey, ls); }
    }
    if (s) setRowCount(Math.max(1, parseInt(s, 10) || rows.length));
  }, [loaded, countKey, rows.length]);
  const addRow = () => {
    const n = rowCount + 1;
    setRowCount(n);
    save(countKey, String(n));
  };
  const removeRow = (ri) => {
    for (let r = ri; r < rowCount - 1; r++) {
      for (let ci = 0; ci < columns.length; ci++) {
        const nextVal = get(cellKey(r + 1, ci));
        save(cellKey(r, ci), nextVal ?? "");
      }
    }
    for (let ci = 0; ci < columns.length; ci++) save(cellKey(rowCount - 1, ci), "");
    const n = Math.max(1, rowCount - 1);
    setRowCount(n);
    save(countKey, String(n));
    setVersion((v) => v + 1);
  };
  return (
    <div>
      <div className="overflow-x-auto rounded-xl border border-line">
        <table className="w-full border-collapse text-sm" style={{ minWidth: Math.max(640, columns.length * 300) }}>
          <thead>
            <tr>
              {columns.map((c, ci) => (
                <th key={ci} className="border-b border-r border-white/40 bg-bordeaux-tint px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-bordeaux">{c}</th>
              ))}
              {!readOnly && <th className="w-9 bg-bordeaux-tint" aria-hidden />}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: rowCount }).map((_, ri) => (
              <tr key={ri} className="group align-top">
                {columns.map((_c, ci) => (
                  <td key={ci} className="border-b border-r border-line bg-white p-0">
                    <TableCell key={`${ri}-${ci}-${version}`} storageKey={cellKey(ri, ci)} defaultValue={(rows[ri] && rows[ri][ci]) || ""} />
                  </td>
                ))}
                {!readOnly && (
                  <td className="border-b border-line bg-white text-center align-middle">
                    <button
                      type="button"
                      onClick={() => removeRow(ri)}
                      data-testid={`table-remove-row-${index}-${ri}`}
                      title="Supprimer la ligne"
                      className="px-2 text-lg leading-none text-ink-muted opacity-0 transition hover:text-bordeaux group-hover:opacity-100"
                    >
                      ×
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!readOnly && (
        <button
          type="button"
          onClick={addRow}
          data-testid={`table-add-row-${index}`}
          className="mt-2.5 inline-flex items-center gap-1.5 rounded-lg border border-dashed border-bordeaux-border px-3.5 py-2 text-sm font-medium text-bordeaux transition hover:bg-bordeaux-tint/30"
        >
          <Plus size={15} /> Ajouter une ligne
        </button>
      )}
    </div>
  );
}

function PageLinkField({ ckey, link, setLink }) {
  const [saved, setSaved] = useState(link);
  useEffect(() => { setSaved((s) => s || link); }, [link]);
  const save = async () => {
    if (link.trim() === saved.trim()) return;
    try {
      await api.put(`/client/page-completion/${ckey}/link`, { link });
      setSaved(link);
      toast.success("Lien enregistré ✨");
    } catch (_) {
      toast.error("Oups, réessaie dans un instant.");
    }
  };
  return (
    <div data-testid={`page-link-block-${ckey}`} className="!mt-12 rounded-3xl border border-bordeaux-border bg-white p-5 shadow-card sm:p-6">
      <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-bordeaux">Le lien de ta page</p>
      <p className="mt-1 text-sm font-light text-ink-muted">Colle ici le lien de ta page pour que je puisse la voir.</p>
      <div className="mt-3 flex flex-col gap-2 sm:flex-row">
        <div className="flex flex-1 items-center gap-2 rounded-full border border-line bg-cream/60 px-4 focus-within:border-bordeaux">
          <Link2 size={15} className="shrink-0 text-bordeaux" />
          <input data-testid={`page-link-input-${ckey}`} type="url" value={link} onChange={(e) => setLink(e.target.value)} onBlur={save}
            placeholder="https://..." className="w-full bg-transparent py-2.5 text-sm text-ink outline-none placeholder:text-ink-muted" />
        </div>
        <button data-testid={`page-link-save-${ckey}`} onClick={save} className="rounded-full bg-bordeaux px-5 py-2.5 text-sm font-medium text-white transition hover:bg-bordeaux-dark">Enregistrer</button>
      </div>
      {saved && /^https?:\/\//.test(saved) && (
        <a data-testid={`page-link-open-${ckey}`} href={saved} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-bordeaux hover:underline">Ouvrir ma page <ArrowRight size={12} /></a>
      )}
    </div>
  );
}

export function CompletionToggle({ ckey, title, message, spaced, withLink = false }) {
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [link, setLink] = useState("");
  useEffect(() => {
    api.get(`/client/page-completion/${ckey}`).then(({ data }) => { setDone(!!data.completed); setLink(data.link || ""); }).catch(() => {});
  }, [ckey]);
  const toggle = async () => {
    const next = !done;
    setLoading(true);
    try {
      await api.post(`/client/page-completion/${ckey}`, withLink ? { completed: next, title, message, link } : { completed: next, title, message });
      setDone(next);
      toast.success(next ? "Bravo ! Fanny a bien été prévenue 🎉" : "C'est annulé, tu peux reprendre.");
    } catch (_) {
      toast.error("Oups, réessaie dans un instant.");
    } finally {
      setLoading(false);
    }
  };
  return (
    <>
    {withLink && <PageLinkField ckey={ckey} link={link} setLink={setLink} />}
    <div data-testid={`complete-block-${ckey}`} className={`${withLink ? "!mt-4" : spaced ? "!mt-12" : "!mt-0"} !mb-12 rounded-3xl bg-gradient-to-br from-[#7B113A] via-[#5A1530] to-[#2D181E] p-5 shadow-[0_12px_40px_rgba(123,17,58,0.3)] sm:p-6`}>
      {done ? (
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-4">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-bordeaux text-white shadow-[0_0_20px_rgba(123,17,58,0.6)]"><CheckCircle2 size={22} /></span>
            <div>
              <p className="font-medium text-white">Exercice terminé ✨</p>
              <p className="text-sm font-light text-white/60">Fanny a bien été prévenue.</p>
            </div>
          </div>
          <button data-testid={`complete-toggle-${ckey}`} onClick={toggle} disabled={loading}
            className="shrink-0 rounded-full px-4 py-2 text-sm text-white/70 transition-colors hover:bg-white/5 hover:text-white disabled:opacity-60">
            Annuler
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white/5 text-white ring-1 ring-white/10"><Check size={20} /></span>
            <p className="font-light text-white/90">Tu as terminé cet exercice ? Clique ici pour me prévenir</p>
          </div>
          <button data-testid={`complete-toggle-${ckey}`} onClick={toggle} disabled={loading}
            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-bordeaux px-6 py-3 text-sm font-medium text-white ring-1 ring-white/25 shadow-[0_0_20px_rgba(123,17,58,0.6)] transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60">
            <Check size={16} /> J'ai terminé
          </button>
        </div>
      )}
    </div>
    </>
  );
}

async function compressImage(file) {
  try {
    const bmp = await createImageBitmap(file);
    const scale = Math.min(1, 1600 / Math.max(bmp.width, bmp.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bmp.width * scale);
    canvas.height = Math.round(bmp.height * scale);
    canvas.getContext("2d").drawImage(bmp, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((r) => canvas.toBlob(r, "image/jpeg", 0.85));
    return blob ? new File([blob], "capture.jpg", { type: "image/jpeg" }) : file;
  } catch (_) {
    return file;
  }
}

function ImageUpload({ storageKey }) {
  const { get, save, uploadFile, readOnly, loaded } = useSub();
  const [img, setImg] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!loaded) return;
    const dbVal = get(storageKey);
    if (dbVal) { setImg(dbVal); return; }
    if (!readOnly) {
      const ls = localStorage.getItem(storageKey);
      if (ls) { setImg(ls); save(storageKey, ls); }
    }
  }, [loaded, storageKey]);
  const onChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const url = await uploadFile(file);
      setImg(url);
      save(storageKey, url);
    } catch (_) {
      toast.error("L'envoi de l'image a échoué, réessaie avec une capture en PNG ou JPG.");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  const remove = () => { setImg(""); save(storageKey, ""); };
  if (readOnly) {
    return (
      <div className="mt-2">
        {img
          ? <img src={img} alt="Capture de la cliente" className="max-h-80 rounded-xl border border-line" />
          : <p className="rounded-xl border border-dashed border-line bg-cream/40 px-4 py-6 text-center text-sm italic text-ink-muted">— Aucune image envoyée —</p>}
      </div>
    );
  }
  return (
    <div className="mt-2">
      {img ? (
        <div className="relative inline-block min-h-[80px] min-w-[160px]">
          <img src={img} alt="Capture" className="max-h-80 rounded-xl border border-line" />
          <button
            type="button"
            onClick={remove}
            data-testid={`remove-image-${storageKey}`}
            className="absolute right-2 top-2 rounded-full bg-white/90 px-3 py-1 text-xs font-medium text-bordeaux shadow-card transition hover:bg-white"
          >
            Retirer
          </button>
        </div>
      ) : (
        <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-bordeaux-border bg-bordeaux-tint/30 px-5 py-8 text-center transition hover:bg-bordeaux-tint/50">
          <ImagePlus size={22} className="text-bordeaux" />
          <span className="text-sm font-medium text-bordeaux">{busy ? "Envoi en cours…" : "Ajouter une capture d'écran"}</span>
          <span className="text-xs font-light text-ink-muted">PNG ou JPG</span>
          <input type="file" accept="image/*" onChange={onChange} disabled={busy} data-testid={`upload-image-${storageKey}`} className="hidden" />
        </label>
      )}
    </div>
  );
}

function PromptBox({ text, label }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (_) {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="overflow-hidden rounded-2xl border border-line bg-white">
      <div className="flex items-center justify-between border-b border-line px-5 py-3">
        <span className="text-sm font-medium text-ink">{label || "Le prompt"}</span>
        <button
          type="button"
          onClick={copy}
          data-testid="copy-prompt-button"
          className="flex items-center gap-2 rounded-full bg-bordeaux px-4 py-2 text-xs font-medium text-white transition hover:bg-bordeaux-dark"
        >
          {copied ? <Check size={14} /> : <Copy size={14} />}
          {copied ? "Copié !" : "Copier"}
        </button>
      </div>
      <pre className="max-h-[28rem] overflow-y-auto whitespace-pre-wrap p-5 font-sans text-sm font-light leading-relaxed text-ink-soft">{text}</pre>
    </div>
  );
}

function Collapse({ title, text, sections, dark = false }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`overflow-hidden rounded-xl border ${dark ? "border-[#590224] bg-white" : "border-line bg-white"}`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        data-testid="collapse-toggle"
        className={`flex w-full items-center justify-between gap-3 px-4 text-left text-sm font-medium ${dark ? "bg-[#590224] py-4 text-white transition hover:bg-[#6a0a30]" : "py-3 text-ink"}`}
      >
        <span>
          {title}
          {dark && <span className="mt-0.5 block text-xs font-light text-white/70">{open ? "Clique pour refermer" : "Clique pour découvrir la structure 👇🏻"}</span>}
        </span>
        <span className={dark ? "flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/15" : ""}>
          <ChevronDown size={16} className={`${dark ? "text-white" : "text-ink-muted"} transition-transform ${open ? "rotate-180" : ""}`} />
        </span>
      </button>
      {open && (
        <div className="space-y-4 border-t border-line px-4 py-4">
          {sections && sections.map((s, i) => {
            const isLead = !s.body || s.body.length === 0;
            if (isLead) {
              return (
                <p key={i} className="rounded-lg bg-cream px-3 py-2 text-center text-xs font-medium tracking-wide text-ink-muted">{s.h}</p>
              );
            }
            return (
              <div key={i} className="rounded-xl border border-line bg-cream/40 p-4">
                {s.h && <p className="text-sm font-semibold text-bordeaux">{s.h}</p>}
                {s.body.map((b, j) => {
                  const isArrow = typeof b === "string" && b.trim().startsWith("➡️");
                  const isUrl = typeof b === "string" && /^https?:\/\//.test(b.trim());
                  if (isArrow) {
                    return <p key={j} className="mt-2.5 rounded-lg bg-bordeaux-tint/50 px-3 py-2 text-sm font-medium leading-relaxed text-bordeaux">{b}</p>;
                  }
                  if (isUrl) {
                    return <p key={j} className="mt-2"><a href={b.trim()} target="_blank" rel="noreferrer" className="break-all text-sm text-bordeaux underline underline-offset-2 transition hover:opacity-80">{b.trim()}</a></p>;
                  }
                  return <p key={j} className="mt-2 text-sm font-light leading-relaxed text-ink-soft">{b}</p>;
                })}
              </div>
            );
          })}
          {text && (text.length > 1 ? (
            <ul className="list-disc space-y-2 pl-5 text-sm font-light leading-relaxed text-ink-soft marker:text-bordeaux">
              {text.map((p, i) => <li key={i}>{p}</li>)}
            </ul>
          ) : (
            text.map((p, i) => <p key={i} className="text-sm font-light leading-relaxed text-ink-soft">{p}</p>)
          ))}
        </div>
      )}
    </div>
  );
}

function Block({ block, slug, index, lockedSet = [] }) {
  const navigate = useNavigate();
  switch (block.t) {
    case "hero":
      return (
        <div className="flex h-44 items-center justify-center overflow-hidden rounded-2xl bg-gradient-to-br from-bordeaux-dark to-bordeaux sm:h-56">
          <span className="logo-elevate text-5xl sm:text-6xl" style={{ color: "#FFFCF6" }}>{block.title}</span>
        </div>
      );
    case "audio":
      return (
        <div className="rounded-2xl border border-line bg-white p-5">
          <p className="font-sans text-base font-medium text-ink">{block.title}</p>
          {block.url
            ? <audio controls src={block.url} className="mt-3 w-full" />
            : <p className="mt-2 text-sm font-light italic text-ink-muted">Audio en cours d'ajout…</p>}
        </div>
      );
    case "linkcard": {
      const target = block.lockKey || block.to.split("/").pop();
      const isLocked = lockedSet.includes(target);
      const LC = {
        gold: "border-amber-200 bg-amber-50 hover:bg-amber-100 text-amber-800",
      };
      const lc = LC[block.tone] || "border-bordeaux-border bg-bordeaux-tint/50 hover:bg-bordeaux-tint text-bordeaux";
      if (isLocked) {
        return (
          <button
            onClick={() => toast("🔒 Cette ressource sera débloquée bientôt par Fanny")}
            data-testid={`linkcard-${target}`}
            className="flex w-full cursor-default items-center gap-4 rounded-2xl border border-line bg-cream/60 p-4 text-left"
          >
            <ProgIcon slug={target} locked />
            <div className="flex-1">
              <p className="font-sans text-base font-medium text-ink-muted">{block.title}</p>
              <p className="text-sm font-light text-ink-muted">Débloqué bientôt par Fanny</p>
            </div>
            <Lock size={16} className="text-ink-muted" />
          </button>
        );
      }
      return (
        <button
          onClick={() => navigate(block.to)}
          data-testid={`linkcard-${target}`}
          className={`flex w-full items-center gap-4 rounded-2xl border p-4 text-left shadow-card transition ${lc}`}
        >
          <ProgIcon slug={target} />
          <div className="flex-1">
            <p className="font-sans text-base font-medium">{block.title}</p>
            <p className="text-sm font-light text-ink-soft">{block.subtitle}</p>
          </div>
          <ChevronRight size={16} />
        </button>
      );
    }
    case "consigne":
      return (
        <div className="rounded-2xl border border-line bg-white p-5">
          <p className="eyebrow">Consignes</p>
          {block.text.map((p, i) => (
            <p key={i} className="mt-2 font-light leading-relaxed text-ink-soft">{p}</p>
          ))}
        </div>
      );
    case "note": {
      if (block.tone === "brand") {
        return (
          <div data-testid="note-brand" className="rounded-2xl bg-[#590224] p-5 text-white shadow-[0_10px_30px_rgba(89,2,36,0.25)] sm:p-6">
            {block.title && <p className="text-base font-medium">{block.title}</p>}
            {block.text.map((p, i) => <p key={i} className="mt-2 font-light leading-relaxed text-white/85">{p}</p>)}
            {block.button && (
              <button type="button" data-testid="note-brand-button" onClick={() => navigate(block.button.to)} className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#FFFCF6] px-5 py-2.5 text-sm font-medium text-[#7B113A] transition-transform hover:-translate-y-0.5">
                {block.button.label} <ArrowRight size={15} />
              </button>
            )}
          </div>
        );
      }
      const TONES = {
        red: "border-rose-200 bg-rose-50",
        white: "border-line bg-white",
        green: "border-emerald-200 bg-emerald-50",
      };
      const toneCls = TONES[block.tone] || "border-bordeaux-border bg-bordeaux-tint/30";
      return (
        <div className={`rounded-2xl border p-5 ${toneCls}`}>
          {block.title && <p className="font-medium text-bordeaux">{block.title}</p>}
          {block.text && block.text.map((p, i) => (
            <p key={i} className="mt-2 font-light leading-relaxed text-ink-soft">{renderRich(p)}</p>
          ))}
          {block.list && (
            <ul className="mt-2 list-disc space-y-1.5 pl-5 font-light text-ink-soft">
              {block.list.map((l, i) => {
                const isUrl = typeof l === "string" && /^https?:\/\//.test(l.trim());
                return (
                  <li key={i}>
                    {isUrl
                      ? <a href={l.trim()} target="_blank" rel="noreferrer" className="break-all text-bordeaux underline underline-offset-2 transition hover:opacity-80">{l.trim()}</a>
                      : l}
                  </li>
                );
              })}
            </ul>
          )}
          {block.sections && block.sections.map((s, i) => (
            <div key={i} className="mt-4">
              <p className="text-sm font-medium text-ink">{s.h}</p>
              <ul className="mt-1.5 list-disc space-y-1 pl-5 font-light text-ink-soft">
                {s.items.map((it, j) => <li key={j}>{it}</li>)}
              </ul>
            </div>
          ))}
        </div>
      );
    }
    case "prompt":
      return <PromptBox text={block.text} label={block.label} />;
    case "fiche": {
      const scheme = block.color === "green"
        ? { box: "border-[#B8D8BE] bg-[#EAF5EC]", title: "text-[#2E7D46]" }
        : { box: "border-[#EBB8B8] bg-[#FBEAEA]", title: "text-[#B03A3A]" };
      return (
        <div className={`space-y-6 rounded-2xl border p-5 sm:p-6 ${scheme.box}`}>
          <p className={`font-medium ${scheme.title}`}>{block.title}</p>
          {block.sections.map((s, si) => (
            <div key={si} className="space-y-4">
              <p className="text-sm font-semibold text-ink">{s.h}</p>
              {s.items.map((q, qi) => (
                <div key={qi}>
                  <p className="text-sm font-light text-ink-soft">{q}</p>
                  <Answer storageKey={`pp-${slug}-${index}-${si}-${qi}`} boxClass="border-line bg-white/70 focus:border-bordeaux focus:bg-white" />
                </div>
              ))}
            </div>
          ))}
        </div>
      );
    }
    case "step":
      return (
        <div className="rounded-[28px] border border-line bg-white p-6 shadow-card sm:p-8">
          <div className="flex items-center gap-4">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-bordeaux font-sans text-lg font-medium text-white shadow-[0_8px_20px_rgba(123,17,58,0.3)]">{block.n}</span>
            <h2 className="font-sans text-xl font-medium text-ink">{block.title}</h2>
          </div>
          {(block.text || []).map((p, i) => <p key={i} className="mt-4 font-light leading-relaxed text-ink-soft">{renderRich(p)}</p>)}
          {block.list && (
            <div className="mt-4 space-y-2">
              {block.list.map((l, i) => (
                <div key={i} className="flex items-start gap-3 rounded-2xl bg-cream px-4 py-3">
                  <CheckCircle2 size={18} className="mt-0.5 shrink-0 text-bordeaux" />
                  <span className="text-sm font-light text-ink">{renderRich(l)}</span>
                </div>
              ))}
            </div>
          )}
          {(block.after || []).map((p, i) => <p key={i} className="mt-4 font-light leading-relaxed text-ink-soft">{renderRich(p)}</p>)}
          {block.buttons && (
            <div className="mt-5 flex flex-wrap gap-3">
              {block.buttons.map((b, i) => b.url ? (
                <a key={i} href={b.url} target="_blank" rel="noreferrer" data-testid={`step-btn-${block.n}-${i}`} className="inline-flex items-center gap-2 rounded-full border border-bordeaux-border bg-white py-1.5 pl-1.5 pr-5 text-sm font-medium text-bordeaux transition-transform hover:-translate-y-0.5"><span className="flex h-7 w-7 items-center justify-center rounded-full bg-bordeaux-tint"><ArrowRight size={14} /></span>{b.label}</a>
              ) : (
                <button key={i} onClick={() => navigate(b.to)} data-testid={`step-btn-${block.n}-${i}`} className="inline-flex items-center gap-2 rounded-full bg-bordeaux py-1.5 pl-1.5 pr-5 text-sm font-medium text-white transition-transform hover:-translate-y-0.5"><span className="flex h-7 w-7 items-center justify-center rounded-full bg-white/15"><ArrowRight size={14} /></span>{b.label}</button>
              ))}
            </div>
          )}
        </div>
      );
    case "alert":
      return (
        <div className="relative overflow-hidden rounded-[28px] p-6 text-white sm:p-8" style={{ backgroundColor: "#2D181E", backgroundImage: "radial-gradient(circle at 12% 0%, rgba(123,17,58,0.55), transparent 55%), radial-gradient(circle at 100% 100%, rgba(89,2,36,0.6), transparent 50%)" }}>
          <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-white/80"><span className="h-1.5 w-1.5 rounded-full bg-[#F3D2D9] ring-2 ring-[#F3D2D9]/25" /> À retenir</span>
          <p className="mt-4 font-sans text-xl font-medium">{block.title}</p>
          {block.text.map((p, i) => <p key={i} className="mt-2 text-sm font-light leading-relaxed text-white/75">{p}</p>)}
        </div>
      );
    case "heading":
      return (
        <div className="border-t border-line pt-12 mt-10">
          <h2 className="font-sans text-xl font-medium text-ink">{block.text}</h2>
        </div>
      );
    case "paragraph":
      return <p className="font-light leading-relaxed text-ink-soft">{renderRich(block.text)}</p>;
    case "video":
      return (
        <div className="aspect-video overflow-hidden rounded-2xl border border-line bg-black">
          <iframe src={ytEmbed(block.url)} title="Vidéo" className="h-full w-full" allowFullScreen frameBorder="0" />
        </div>
      );
    case "awareness":
      return (
        <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-5">
          {block.items.map((it, i) => (
            <div key={i} className="flex min-h-[210px] flex-col rounded-2xl p-3 text-center sm:min-h-[300px] sm:p-4" style={{ backgroundColor: it.bg }}>
              <div className="rounded-xl bg-[#1f1a1b] px-2 py-2.5">
                <p className="font-serif text-base font-semibold leading-tight text-[#FFFCF6] sm:text-lg">{it.title}</p>
              </div>
              {it.sub && <p className={`mt-2 font-serif text-sm font-semibold ${it.dark ? "text-[#590224]" : "text-[#FFFCF6]"}`}>{it.sub}</p>}
              <p className={`flex flex-1 items-center justify-center px-1 py-4 text-xs leading-relaxed sm:text-sm ${it.dark ? "text-[#590224]" : "text-white"}`}>{it.desc}</p>
              <p className={`text-sm font-medium ${it.dark ? "text-[#590224]/60" : "text-white/80"}`}>{i + 1}</p>
            </div>
          ))}
        </div>
      );
    case "image":
      return <img src={block.url} alt={block.alt || ""} className="w-full rounded-2xl border border-line" loading="lazy" />;
    case "gap":
      return <div className="h-12" aria-hidden />;
    case "divider":
      return (
        <div className="py-12" aria-hidden>
          <div className="h-px w-full bg-line" />
        </div>
      );
    case "resource": {
      const insta = block.tone === "insta";
      const cls = insta
        ? "border-bordeaux-border bg-bordeaux-tint font-medium text-bordeaux hover:bg-bordeaux-tint/70"
        : "border-line text-bordeaux hover:bg-bordeaux-tint";
      return (
        <a href={block.url} target="_blank" rel="noreferrer"
          className={`flex items-center gap-2 rounded-lg border px-4 py-2.5 text-sm transition ${cls}`}>
          <Link2 size={15} /> {block.label}
        </a>
      );
    }
    case "question":
      return (
        <div className={block.tall ? "pt-4" : ""}>
          {Array.isArray(block.q) ? (
            block.q.map((line, i) => (
              <p key={i} className={`text-sm font-medium text-ink ${i > 0 ? "mt-1.5" : ""}`}>
                {block.n && i === 0 ? `${block.n}. ` : ""}{line}
              </p>
            ))
          ) : (
            <p className="text-sm font-medium text-ink">{block.n ? `${block.n}. ` : ""}{block.q}</p>
          )}
          {block.help && <p className="mt-1 text-sm font-light text-ink-muted">{block.help}</p>}
          <Answer storageKey={`pp-${slug}-${index}`} tall={block.tall} placeholder={block.placeholder} />
        </div>
      );
    case "collapse":
      return <Collapse title={block.title} text={block.text} sections={block.sections} dark={block.dark} />;
    case "answerbox":
      return (
        <div>
          {block.help && <p className="mb-1.5 text-sm font-light text-ink-muted">{block.help}</p>}
          <Answer storageKey={`pp-${slug}-${index}`} />
        </div>
      );
    case "upload":
      return (
        <div>
          <p className="text-sm font-medium text-ink">{block.n ? `${block.n}. ` : ""}{block.q}</p>
          <ImageUpload storageKey={`pp-${slug}-${index}`} />
        </div>
      );
    case "editfields":
      return <EditFields title={block.title} lines={block.lines} fields={block.fields} slug={slug} index={index} />;
    case "table":
      return (
        <div className={block.wide ? "lg:w-[calc(100%+22rem)]" : ""}>
          <EditableTable columns={block.columns} rows={block.rows} slug={slug} index={index} />
        </div>
      );
    case "complete":
      return <CompletionToggle ckey={block.key || slug} title={block.title || slug} message={block.message} spaced={block.spaced} />;
    default:
      return null;
  }
}

export default function ProgrammePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [lockedSet, setLockedSet] = useState([]);
  useEffect(() => {
    api.get("/client/access").then(({ data }) => setLockedSet(data.locked_resources || [])).catch(() => {});
  }, []);

  if (lockedSet.includes(slug)) {
    return (
      <div className="max-w-2xl">
        <StickyBack onClick={() => navigate("/espace/parcours")}>Le programme</StickyBack>
        <div className="mt-8 flex flex-col items-center gap-4 rounded-2xl border border-line bg-white p-10 text-center shadow-card">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-bordeaux-tint text-bordeaux"><Lock size={24} /></span>
          <h1 className="font-sans text-2xl font-medium text-ink">Ressource bientôt disponible</h1>
          <p className="max-w-md font-light text-ink-soft">Cette ressource sera débloquée prochainement par Fanny. Continue les étapes déjà accessibles en attendant ✨</p>
        </div>
      </div>
    );
  }

  if (slug === SUCCESS_FOUNDATIONS.slug) {
    return (
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl space-y-8">
        <StickyBack onClick={() => navigate("/espace/parcours/1")}>Mise en place & Stratégie</StickyBack>
        <div>
          <p className="eyebrow">Semaine 1 → 2</p>
          <div className="flex items-center gap-4">
            <ProgIcon slug={SUCCESS_FOUNDATIONS.slug} size="lg" />
            <h1 className="font-sans text-3xl font-medium text-ink sm:text-4xl">The Success Foundations</h1>
          </div>
          <p className="mt-2 font-light text-ink-soft">{SUCCESS_FOUNDATIONS.intro}</p>
        </div>
        <div className="aspect-video overflow-hidden rounded-2xl border border-line bg-black">
          <iframe src={ytEmbed(SUCCESS_FOUNDATIONS.video)} title="Vidéo d'introduction" className="h-full w-full" allowFullScreen frameBorder="0" />
        </div>
        <div className="space-y-3">
          {SUCCESS_FOUNDATIONS.children.map((c) => {
            const childLocked = lockedSet.includes(c.slug);
            return (
            <button
              key={c.slug}
              data-testid={`foundation-child-${c.slug}`}
              onClick={() => childLocked ? toast("🔒 Cette ressource sera débloquée bientôt par Fanny") : navigate(c.to || `/espace/programme/${c.slug}`)}
              className={`flex w-full items-center gap-4 rounded-2xl border p-4 text-left shadow-card transition ${childLocked ? "cursor-default border-line bg-cream/60" : "border-line bg-white hover:border-bordeaux-border hover:bg-bordeaux-tint/20"}`}
            >
              <ProgIcon slug={c.slug} locked={childLocked} />
              <div className="flex-1">
                <p className={`font-sans text-base font-medium ${childLocked ? "text-ink-muted" : "text-ink"}`}>{c.title}</p>
                <p className="text-sm font-light text-ink-muted">{childLocked ? "Débloqué bientôt par Fanny" : c.subtitle}</p>
              </div>
              {childLocked ? <Lock size={16} className="text-ink-muted" /> : <ChevronRight size={16} className="text-ink-muted" />}
            </button>
            );
          })}
        </div>
      </motion.div>
    );
  }

  const page = PAGES[slug];
  if (!page) {
    return (
      <div className="max-w-2xl">
        <button onClick={() => navigate("/espace/parcours")} className="text-sm text-bordeaux">← Le programme</button>
        <p className="mt-4 text-ink-muted">Page introuvable.</p>
      </div>
    );
  }

  return (
    <SubmissionsProvider page={slug}>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="max-w-[1100px]"><AnnotateLayout narrow><div className="space-y-6">
        <StickyBack onClick={() => navigate(page.back ? page.back.to : `/espace/programme/${SUCCESS_FOUNDATIONS.slug}`)}>{page.back ? page.back.label : "The Success Foundations"}</StickyBack>
        <div className="flex items-center gap-3">
          <ProgIcon slug={page.slug} size="lg" />
          <h1 className="font-sans text-3xl font-medium text-ink sm:text-4xl">{page.title}</h1>
        </div>
        <div className="space-y-5">
          {page.blocks.map((block, i) => (
            <Block key={i} block={block} slug={slug} index={i} lockedSet={lockedSet} />
          ))}
        </div>
        <div className="rounded-2xl border border-dashed border-bordeaux-border bg-bordeaux-tint/20 px-5 py-4 text-sm font-light text-bordeaux">
          💾 Tes réponses sont enregistrées automatiquement. Tu pourras y revenir à tout moment.
        </div>
      </div></AnnotateLayout></motion.div>
    </SubmissionsProvider>
  );
}

// ---------------------------------------------------------------------------
// Admin read-only viewer: renders a programme page exactly as the client sees
// it, pre-filled with that client's saved answers. Used inside ClientDossier.
// ---------------------------------------------------------------------------
// Admin comment card + margin layout — comments align to the commented text.
function fmtCommentDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "short" }).replace(".", "");
  } catch (_) { return ""; }
}

function CommentCard({ c, admin, updateComment, validateComment, removeComment, viewer = "admin" }) {
  const [editing, setEditing] = useState(!c.validated);
  const [draft, setDraft] = useState(c.text || "");
  const [menu, setMenu] = useState(false);
  const mine = (c.author || "admin") === viewer;
  const isCoach = (c.author || "admin") === "admin";
  const who = isCoach ? admin.name : (viewer === "cliente" ? "Moi" : c.author_name || "Ta cliente");
  useEffect(() => { if (c.validated) setEditing(false); }, [c.validated]);

  if (editing && mine) {
    return (
      <div data-testid={`comment-${c.id}`} className="rounded-2xl p-4 text-white shadow-[0_12px_32px_rgba(45,24,30,0.25)]" style={DARK}>
        <textarea
          autoFocus
          value={draft}
          onChange={(e) => { setDraft(e.target.value); updateComment(c.id, e.target.value); }}
          rows={3}
          placeholder="Ton commentaire…"
          data-testid={`comment-input-${c.id}`}
          className="w-full resize-y rounded-xl border border-white/10 bg-white/5 p-2.5 text-sm text-white outline-none placeholder:text-white/30"
        />
        <div className="mt-2 flex items-center justify-between">
          <button onClick={() => removeComment(c.id)} data-testid={`comment-delete-${c.id}`} className="text-xs text-white/50 transition-colors hover:text-white">Supprimer</button>
          <button
            onClick={() => { const t = draft.trim(); if (!t) return; validateComment(c.id, t, true); setEditing(false); }}
            disabled={!draft.trim()}
            data-testid={`comment-validate-${c.id}`}
            className="rounded-full bg-bordeaux px-4 py-1.5 text-xs font-medium text-white shadow-[0_0_14px_rgba(123,17,58,0.6)] transition-opacity disabled:cursor-not-allowed disabled:opacity-40"
          >Valider</button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid={`comment-${c.id}`} className="group relative rounded-2xl p-4 text-white shadow-[0_12px_32px_rgba(45,24,30,0.25)]" style={DARK}>
      <div className="flex items-center gap-2">
        {isCoach
          ? <img src={admin.photo} alt={admin.name} className="h-7 w-7 shrink-0 rounded-full object-cover ring-1 ring-white/20" onError={(e) => { e.currentTarget.style.display = "none"; }} />
          : <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-bordeaux text-xs font-semibold text-white ring-1 ring-white/20">{(c.author_name || "M").charAt(0).toUpperCase()}</span>}
        <span className="text-xs font-semibold tracking-wide text-white">{who}</span>
        <span className="text-xs text-white/40">{fmtCommentDate(c.created_at)}</span>
        {mine && <div className="relative ml-auto">
          <button onClick={() => setMenu((m) => !m)} data-testid={`comment-menu-${c.id}`} className="flex h-6 w-6 items-center justify-center rounded-md text-white/50 opacity-0 transition hover:bg-white/10 hover:text-white group-hover:opacity-100">
            <MoreHorizontal size={16} />
          </button>
          {menu && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setMenu(false)} />
              <div className="absolute right-0 top-7 z-20 w-32 overflow-hidden rounded-lg border border-line bg-white py-1 shadow-editorial">
                <button onClick={() => { setMenu(false); setEditing(true); }} data-testid={`comment-edit-${c.id}`} className="block w-full px-3 py-1.5 text-left text-xs text-ink transition hover:bg-bordeaux-tint">Modifier</button>
                <button onClick={() => { setMenu(false); removeComment(c.id); }} data-testid={`comment-delete-${c.id}`} className="block w-full px-3 py-1.5 text-left text-xs text-ink transition hover:bg-bordeaux-tint">Supprimer</button>
              </div>
            </>
          )}
        </div>}
      </div>
      <ReadMore text={c.text} className="mt-2.5 text-sm font-light leading-relaxed text-white/90" />
      {!mine && c.quote && <p className="mt-2 line-clamp-2 border-l-2 border-[#F3D2D9] pl-2 text-[11px] italic text-white/55">« {c.quote} »</p>}
    </div>
  );
}

export function AnnotateLayout({ children, narrow = false }) {
  const { comments, updateComment, validateComment, removeComment, admin, viewer } = useSub();
  const wrapRef = useRef(null);
  const [tops, setTops] = useState({});
  const has = comments.length > 0;
  useLayoutEffect(() => {
    if (!has || !wrapRef.current) return;
    const measure = () => {
      const base = wrapRef.current.getBoundingClientRect().top;
      const raw = comments.map((c) => {
        const el = wrapRef.current.querySelector(`[data-cid="${c.id}"]`) || (c.key && wrapRef.current.querySelector(`[data-field="${c.key}"]`));
        return { id: c.id, top: el ? el.getBoundingClientRect().top - base : 0 };
      }).sort((a, b) => a.top - b.top);
      let last = -Infinity;
      const next = {};
      raw.forEach((r) => { const t = Math.max(r.top, last + 136); next[r.id] = t; last = t; });
      setTops(next);
    };
    measure();
    const t = setTimeout(measure, 400);
    window.addEventListener("resize", measure);
    return () => { clearTimeout(t); window.removeEventListener("resize", measure); };
  }, [comments, has]);
  return (
    <div ref={wrapRef} className={`relative ${has ? "lg:pr-[324px]" : ""}`}>
      {narrow && !has ? <div className="max-w-3xl">{children}</div> : children}
      {has && (
        <div className="hidden lg:block">
          {comments.map((c) => (
            <div key={c.id} className="absolute right-0 w-[300px] transition-all duration-200" style={{ top: tops[c.id] ?? 0 }}>
              <CommentCard c={c} admin={admin} viewer={viewer} updateComment={updateComment} validateComment={validateComment} removeComment={removeComment} />
            </div>
          ))}
        </div>
      )}
      {has && (
        <div className="mt-6 space-y-3 lg:hidden">
          <p className="text-xs font-semibold uppercase tracking-wider text-ink-soft">Commentaires</p>
          {comments.map((c) => <CommentCard key={c.id} c={c} admin={admin} viewer={viewer} updateComment={updateComment} validateComment={validateComment} removeComment={removeComment} />)}
        </div>
      )}
    </div>
  );
}

export function AnnotatableText({ storageKey, fallback }) {
  const { getAnn, saveAnn, addComment, annLoaded } = useSub();
  if (!annLoaded) return <div className="h-11 rounded-xl border border-line bg-white" />;
  const a = getAnn(storageKey);
  const initial = hasAnn(a) ? a : toHtml(fallback ?? "");
  return <RichTextField testid={`annotate-${storageKey}`} fieldKey={storageKey} onComment={addComment} initialHtml={initial} onChange={(html) => saveAnn(storageKey, html)} />;
}

const FILLABLE = ["question", "answerbox", "upload", "editfields", "table", "fiche"];
const NONE = "— pas encore de réponse —";

function pageToText(page, get, name) {
  const s = page.slug;
  const ans = (k, def) => { const v = htmlToText(get(k) ?? def ?? "").trim(); return v || NONE; };
  const out = [`${page.title}${name ? ` — ${name}` : ""}`, ""];
  page.blocks.forEach((b, i) => {
    const k = `pp-${s}-${i}`;
    if (b.t === "heading") out.push("", b.text, "");
    else if (b.t === "question") out.push(`${b.n ? `${b.n}. ` : ""}${Array.isArray(b.q) ? b.q.join(" ") : b.q}`, ans(k), "");
    else if (b.t === "answerbox") out.push(b.help || "Réponse", ans(k), "");
    else if (b.t === "upload") out.push(`${b.n ? `${b.n}. ` : ""}${b.q}`, get(k) ? "[capture d'écran ajoutée]" : NONE, "");
    else if (b.t === "editfields") {
      if (b.title) out.push(b.title);
      (b.fields || []).forEach((f, j) => out.push(`${f.label} :`, ans(`${k}-${j}`, f.value), ""));
      (b.lines || []).forEach((ln, j) => out.push(ans(`${k}-${j}`, ln)));
      out.push("");
    } else if (b.t === "table") {
      const n = Math.max(1, parseInt(get(`${k}-rows`), 10) || b.rows.length);
      for (let r = 0; r < n; r++) {
        out.push(`Ligne ${r + 1}`);
        b.columns.forEach((c, ci) => { const v = htmlToText(get(`${k}-r${r}-c${ci}`) ?? (b.rows[r] && b.rows[r][ci]) ?? "").trim(); out.push(`${c} : ${v || "—"}`); });
        out.push("");
      }
    } else if (b.t === "fiche") {
      out.push(b.title, "");
      b.sections.forEach((sec, si) => { out.push(sec.h); sec.items.forEach((q, qi) => out.push(q, ans(`${k}-${si}-${qi}`), "")); });
    }
  });
  return out.join("\n").replace(/\n{3,}/g, "\n\n").trim();
}

function CopyPageButton({ page, name }) {
  const { get, loaded } = useSub();
  const [done, setDone] = useState(false);
  if (!page.blocks.some((b) => FILLABLE.includes(b.t))) return null;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(pageToText(page, get, name));
      setDone(true);
      toast.success("Contenu de la page copié");
      setTimeout(() => setDone(false), 2000);
    } catch (_) {
      toast.error("La copie a échoué, réessaie.");
    }
  };
  return (
    <button data-testid="copy-page-button" onClick={copy} disabled={!loaded} title="Copier le contenu de la page"
      className="ml-auto inline-flex shrink-0 items-center gap-2 rounded-full border border-bordeaux-border bg-white px-3 py-2 text-xs font-medium text-bordeaux shadow-card transition-colors hover:bg-bordeaux-tint disabled:opacity-50 sm:px-4">
      {done ? <Check size={14} /> : <Copy size={14} />}
      <span className="hidden sm:inline">{done ? "Copié" : "Copier le contenu de la page"}</span>
    </button>
  );
}

export function ProgrammePageView({ slug, clientId, annotate = false, clientName = "" }) {
  const page = PAGES[slug];
  if (!page) {
    return (
      <p className="rounded-xl border border-dashed border-line bg-cream/40 px-4 py-8 text-center text-sm text-ink-muted">
        Cette étape n'a pas d'exercice à remplir (page de navigation ou ressource audio/vidéo).
      </p>
    );
  }
  const body = (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <ProgIcon slug={page.slug} size="lg" />
        <h2 className="font-sans text-2xl font-medium text-ink">{page.title}</h2>
        {clientId && <CopyPageButton page={page} name={clientName} />}
      </div>
      {page.blocks.map((block, i) => (
        <Block key={i} block={block} slug={slug} index={i} lockedSet={[]} />
      ))}
    </div>
  );
  return (
    <SubmissionsProvider page={slug} readOnly clientId={clientId} annotate={annotate}>
      {annotate ? <AnnotateLayout>{body}</AnnotateLayout> : body}
    </SubmissionsProvider>
  );
}
